/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * CZAS OPERATORA (Europe/Warsaw) ↔ CZAS ZAPISU (UTC)
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Operator myśli w czasie warszawskim, kontrakt i baza w UTC. Między nimi stoją dwa dni w roku,
 * w których zwykła arytmetyka „odejmij godzinę" kłamie:
 *
 * ┌─ WIOSNA — godzina, której NIE MA ───────────────────────────────────────────────────────────┐
 * │ Ostatnia niedziela marca, 02:00 → 03:00. Godzina 02:30 nie istnieje. Konwersja „na ślepo"   │
 * │ dałaby jakąś godzinę UTC i panel zapisałby start, którego operator nie wybrał.              │
 * │ Tu: NIEISTNIEJACA — odmowa, z wyjaśnieniem.                                                 │
 * └─────────────────────────────────────────────────────────────────────────────────────────────┘
 * ┌─ JESIEŃ — godzina, która jest DWA RAZY ─────────────────────────────────────────────────────┐
 * │ Ostatnia niedziela października, 03:00 → 02:00. Godzina 02:30 zdarza się dwukrotnie: raz w  │
 * │ czasie letnim (UTC+2), raz w zimowym (UTC+1). Wybranie jednej po cichu to wybranie za       │
 * │ operatora. Tu: DWUZNACZNA — obie możliwości, a wybór należy do człowieka.                   │
 * └─────────────────────────────────────────────────────────────────────────────────────────────┘
 *
 * Metoda bez biblioteki dat: dla danej godziny ściennej sprawdzamy oba możliwe przesunięcia
 * Warszawy (+60 i +120 minut) i zostawiamy te, które PO ODWROTNEJ konwersji dają tę samą godzinę
 * ścienną. Zero trafień = godzina nie istnieje, dwa = występuje dwukrotnie. Rozstrzyga baza stref
 * czasowych przeglądarki (ICU), a nie nasza tablica dat zmiany czasu.
 */

export const STREFA = 'Europe/Warsaw';

/** Godzina ścienna w Warszawie. Miesiąc 1–12. */
export interface CzasLokalny {
  rok: number;
  miesiac: number;
  dzien: number;
  godzina: number;
  minuta: number;
}

export type WynikKonwersji =
  | { rodzaj: 'JEDNOZNACZNA'; utc: Date }
  | { rodzaj: 'NIEISTNIEJACA' }
  /** `letnia` (UTC+2) jest wcześniejsza w czasie niż `zimowa` (UTC+1). */
  | { rodzaj: 'DWUZNACZNA'; letnia: Date; zimowa: Date };

const FORMAT = new Intl.DateTimeFormat('en-GB', {
  timeZone: STREFA,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** Godzina ścienna w Warszawie dla chwili UTC. */
export function utcNaWarszawe(utc: Date): CzasLokalny {
  const p: Record<string, number> = {};
  for (const czesc of FORMAT.formatToParts(utc)) {
    if (czesc.type !== 'literal') p[czesc.type] = Number(czesc.value);
  }
  return {
    rok: p.year ?? NaN,
    miesiac: p.month ?? NaN,
    dzien: p.day ?? NaN,
    godzina: p.hour ?? NaN,
    minuta: p.minute ?? NaN,
  };
}

function rowne(a: CzasLokalny, b: CzasLokalny): boolean {
  return a.rok === b.rok && a.miesiac === b.miesiac && a.dzien === b.dzien
    && a.godzina === b.godzina && a.minuta === b.minuta;
}

/** Godzina ścienna w Warszawie → chwila UTC, z jawnym rozróżnieniem przypadków DST. */
export function warszawaNaUtc(l: CzasLokalny): WynikKonwersji {
  const naiwna = Date.UTC(l.rok, l.miesiac - 1, l.dzien, l.godzina, l.minuta);
  const trafienia = [120, 60]
    .map((przesuniecie) => new Date(naiwna - przesuniecie * 60_000))
    .filter((kandydat) => rowne(utcNaWarszawe(kandydat), l));

  const [pierwsza, druga] = trafienia;
  if (!pierwsza) return { rodzaj: 'NIEISTNIEJACA' };
  if (!druga) return { rodzaj: 'JEDNOZNACZNA', utc: pierwsza };
  // Kolejność w `trafienia` = przesunięcie +120 (letnia) przed +60 (zimowa).
  return { rodzaj: 'DWUZNACZNA', letnia: pierwsza, zimowa: druga };
}

// ── Wartość pola <input type="datetime-local"> ↔ CzasLokalny ─────────────────────────────────

/** `2026-10-25T02:30` → CzasLokalny; `null` dla wartości niepełnej albo niepoprawnej. */
export function zPolaDaty(wartosc: string): CzasLokalny | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(wartosc.trim());
  if (!m) return null;
  const [, rok, miesiac, dzien, godzina, minuta] = m.map(Number);
  if (!rok || !miesiac || !dzien || godzina === undefined || minuta === undefined) return null;
  if (miesiac > 12 || dzien > 31 || godzina > 23 || minuta > 59) return null;
  return { rok, miesiac, dzien, godzina, minuta };
}

const dwa = (n: number): string => String(n).padStart(2, '0');

/** CzasLokalny → wartość pola daty. */
export function doPolaDaty(l: CzasLokalny): string {
  return `${l.rok}-${dwa(l.miesiac)}-${dwa(l.dzien)}T${dwa(l.godzina)}:${dwa(l.minuta)}`;
}

/** Czytelny zapis dla operatora, np. „25.10.2026, 02:30". */
export function opisLokalny(l: CzasLokalny): string {
  return `${dwa(l.dzien)}.${dwa(l.miesiac)}.${l.rok}, ${dwa(l.godzina)}:${dwa(l.minuta)}`;
}

// ── Szybkie akcje ────────────────────────────────────────────────────────────────────────────

/** Bieżąca chwila przycięta do pełnej minuty — pole daty nie zna sekund. */
export function odTeraz(teraz: Date): Date {
  return new Date(Math.floor(teraz.getTime() / 60_000) * 60_000);
}

/**
 * Koniec bieżącego dnia w Warszawie jako PÓŁNOC NASTĘPNEGO DNIA.
 *
 * Reguła aktywności to `now < endsAt` — koniec jest WYŁĄCZNY. „Do końca dnia" znaczy więc
 * 00:00 dnia następnego, a nie 23:59, które gubiłoby ostatnią minutę. Północ w Warszawie nigdy
 * nie wypada w zmianie czasu (ta dzieje się o 02:00/03:00), więc konwersja jest jednoznaczna.
 */
export function doKoncaDnia(teraz: Date): Date {
  const dzis = utcNaWarszawe(teraz);
  const jutro = new Date(Date.UTC(dzis.rok, dzis.miesiac - 1, dzis.dzien + 1));
  const wynik = warszawaNaUtc({
    rok: jutro.getUTCFullYear(),
    miesiac: jutro.getUTCMonth() + 1,
    dzien: jutro.getUTCDate(),
    godzina: 0,
    minuta: 0,
  });
  if (wynik.rodzaj !== 'JEDNOZNACZNA') throw new Error('Północ w Warszawie okazała się niejednoznaczna');
  return wynik.utc;
}

/**
 * 24 godziny RZECZYWISTEGO czasu od startu. W dniu zmiany czasu godzina ścienna końca różni się
 * od startu o godzinę — i tak ma być: okno trwa 24 h, a nie „do tej samej godziny jutro".
 */
export function za24h(start: Date): Date {
  return new Date(start.getTime() + 24 * 3_600_000);
}

// ── Walidacja okna ───────────────────────────────────────────────────────────────────────────

export interface Okno {
  start: Date | null;
  koniec: Date | null;
  /** Świadoma decyzja operatora. Brak końca bez tej zgody to pomyłka, nie wybór. */
  bezKonca: boolean;
}

export type WynikOkna = { ok: true } | { ok: false; powod: string };

export function walidujOkno(o: Okno): WynikOkna {
  if (!o.start) return { ok: false, powod: 'Ustaw początek.' };
  if (o.koniec === null) {
    return o.bezKonca
      ? { ok: true }
      : { ok: false, powod: 'Ustaw koniec albo świadomie zaznacz „bez daty końcowej".' };
  }
  if (o.koniec.getTime() <= o.start.getTime()) {
    return { ok: false, powod: 'Koniec musi być później niż początek.' };
  }
  return { ok: true };
}
